// Generated from legacy db.ts to keep files under 500 lines.
import { sql } from "../../base";
import type { RegularTaskHistory } from "../../types";
import { recalculateTaskTimeSpent } from "../timeEntries";

const regularTaskHistoryModule = {
  async recordGeneration(templateId: string, generatedTaskId: string, userId: string, date: Date): Promise<RegularTaskHistory> {
    const result = await sql`
      INSERT INTO regular_task_history (task_template_id, generated_task_id, user_id, generated_date)
      VALUES (${templateId}, ${generatedTaskId}, ${userId}, ${date})
      ON CONFLICT (task_template_id, generated_date) DO NOTHING
      RETURNING *
    `;
    return result[0] as RegularTaskHistory;
  },

  async recordCompletion(generatedTaskId: string, userId: string): Promise<void> {
    const now = new Date();
    const today = new Date(now);
    today.setHours(0, 0, 0, 0);
    
    // Recalculate totals so history, averages, and task data all agree
    const timeSpent = await recalculateTaskTimeSpent(generatedTaskId);
    
    // First, check if a history record exists
    const existing = await sql`
      SELECT id, task_template_id
      FROM regular_task_history
      WHERE generated_task_id = ${generatedTaskId}
      AND user_id = ${userId}
      LIMIT 1
    `;
    
    if (existing.length > 0) {
      // Update existing record
      await sql`
        UPDATE regular_task_history
        SET completed_date = ${today},
            completion_time = ${now},
            time_spent = ${timeSpent}
        WHERE generated_task_id = ${generatedTaskId} 
        AND user_id = ${userId}
      `;
    } else {
      // No history record exists - we need to create one
      // First, get the task details to find the template ID
      const task = await sql`
        SELECT id, parent_task_id, is_regular
        FROM tasks
        WHERE id = ${generatedTaskId}
        AND user_id = ${userId}
        LIMIT 1
      `;
      
      if (task.length > 0 && task[0].is_regular) {
        // For regular tasks, parent_task_id should be the template ID
        const templateId = task[0].parent_task_id || task[0].id;
        
        // Create a new history record with completion info
        await sql`
          INSERT INTO regular_task_history (
            task_template_id,
            generated_task_id,
            user_id,
            generated_date,
            completed_date,
            completion_time,
            time_spent
          ) VALUES (
            ${templateId},
            ${generatedTaskId},
            ${userId},
            ${today},
            ${today},
            ${now},
            ${timeSpent}
          )
        `;
      }
    }
  },

  async hasGeneratedToday(templateId: string, userId: string): Promise<boolean> {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const result = await sql`
      SELECT COUNT(*) as count
      FROM regular_task_history
      WHERE task_template_id = ${templateId}
      AND user_id = ${userId}
      AND generated_date = ${today}
    `;
    
    return result[0].count > 0;
  },

  async hasGeneratedForDate(templateId: string, userId: string, date: Date): Promise<boolean> {
    const targetDate = new Date(date);
    targetDate.setHours(0, 0, 0, 0);
    
    const result = await sql`
      SELECT COUNT(*) as count
      FROM regular_task_history
      WHERE task_template_id = ${templateId}
      AND user_id = ${userId}
      AND generated_date = ${targetDate}
    `;
    
    return result[0].count > 0;
  },

  async getHistory(templateId: string, userId: string, limit: number = 30): Promise<RegularTaskHistory[]> {
    const result = await sql`
      SELECT * FROM regular_task_history
      WHERE task_template_id = ${templateId}
      AND user_id = ${userId}
      ORDER BY generated_date DESC
      LIMIT ${limit}
    `;
    return (result || []) as RegularTaskHistory[];
  },
  
  async getLastCompletionByTitle(title: string, userId: string): Promise<Date | null> {
    try {
      const result = await sql`
        SELECT MAX(rth.completion_time) as last_completion
        FROM regular_task_history rth
        JOIN tasks t ON rth.generated_task_id = t.id
        WHERE t.title = ${title}
        AND t.user_id = ${userId}
        AND rth.completion_time IS NOT NULL
      `;
      
      return result[0]?.last_completion || null;
    } catch (error) {
      console.error('Error getting last completion:', error);
      return null;
    }
  },
  
  async getLastCompletionsForTasks(taskTitles: string[], userId: string): Promise<Map<string, Date | null>> {
    try {
      if (taskTitles.length === 0) return new Map();
      
      // Query to find the most recent completion for each task title
      // This looks at ALL tasks with the given titles and finds their completion times
      const placeholders = taskTitles.map((_, i) => `$${i + 1}`).join(', ');
      const params = [...taskTitles, userId, userId];
      const result = await sqlRaw(
        `WITH task_completions AS (
           SELECT t.title, rth.completion_time
           FROM regular_task_history rth
           JOIN tasks t ON t.id = rth.task_template_id
           WHERE t.title IN (${placeholders}) AND t.user_id = $${taskTitles.length + 1} AND rth.user_id = $${taskTitles.length + 2} AND rth.completion_time IS NOT NULL
           UNION ALL
           SELECT t.title, rth.completion_time
           FROM regular_task_history rth
           JOIN tasks t ON t.id = rth.generated_task_id
           WHERE t.title IN (${placeholders}) AND t.user_id = $${taskTitles.length + 1} AND rth.user_id = $${taskTitles.length + 2} AND rth.completion_time IS NOT NULL
         ), ranked AS (
           SELECT title, completion_time,
                  ROW_NUMBER() OVER (PARTITION BY title ORDER BY completion_time DESC) AS rn
           FROM task_completions
         )
         SELECT title, completion_time as last_completion FROM ranked WHERE rn = 1`,
        params
      );
      
      const completionMap = new Map<string, Date | null>();
      result.forEach(row => {
        // Ensure last_completion is converted to a Date object if it exists
        const lastCompletion = row.last_completion 
          ? new Date(row.last_completion) 
          : null;
        completionMap.set(row.title, lastCompletion);
      });
      
      // Add entries for tasks that weren't found
      taskTitles.forEach(title => {
        if (!completionMap.has(title)) {
          completionMap.set(title, null);
        }
      });
      
      return completionMap;
    } catch (error) {
      console.error('Error getting last completions:', error);
      return new Map();
    }
  },
  
  async getHistoryWithTimeEntries(templateId: string, userId: string, limit: number = 30): Promise<any[]> {
    const result = await sql`
      SELECT 
        rth.*,
        t.title,
        t.description,
        COALESCE(
          (SELECT json_agg(json_build_object(
            'id', te.id,
            'start_time', te.start_time,
            'end_time', te.end_time,
            'notes', te.notes,
            'duration_minutes', EXTRACT(EPOCH FROM (te.end_time - te.start_time)) / 60
          ) ORDER BY te.start_time)
          FROM time_entries te
          WHERE te.task_id = rth.generated_task_id
          ), '[]'::json
        ) as time_entries,
        COALESCE(rth.time_spent, 0) as total_minutes
      FROM regular_task_history rth
      LEFT JOIN tasks t ON t.id = rth.generated_task_id
      WHERE rth.task_template_id = ${templateId}
      AND rth.user_id = ${userId}
      ORDER BY rth.generated_date DESC
      LIMIT ${limit}
    `;
    return result || [];
  },

  async getHistoryByTaskProperties(title: string, description: string | null, frequency: string, priority: string, userId: string, limit: number = 30): Promise<any[]> {
    // Find all task IDs that match these properties
    const result = await sql`
      WITH matching_task_ids AS (
        SELECT DISTINCT id
        FROM tasks
        WHERE title = ${title}
        AND COALESCE(description, '') = ${description || ''}
        AND frequency = ${frequency}
        AND priority = ${priority}
        AND user_id = ${userId}
        AND is_regular = true
      )
      SELECT 
        rth.*,
        t.title,
        t.description,
        COALESCE(
          (SELECT json_agg(json_build_object(
            'id', te.id,
            'start_time', te.start_time,
            'end_time', te.end_time,
            'notes', te.notes,
            'duration_minutes', EXTRACT(EPOCH FROM (te.end_time - te.start_time)) / 60
          ) ORDER BY te.start_time)
          FROM time_entries te
          WHERE te.task_id = rth.generated_task_id
          ), '[]'::json
        ) as time_entries,
        COALESCE(rth.time_spent, 0) as total_minutes
      FROM regular_task_history rth
      LEFT JOIN tasks t ON t.id = rth.generated_task_id
      WHERE rth.task_template_id IN (SELECT id FROM matching_task_ids)
      AND rth.user_id = ${userId}
      ORDER BY rth.generated_date DESC
      LIMIT ${limit}
    `;
    return result || [];
  }
};

export type RegularTaskHistoryModule = typeof regularTaskHistoryModule;

export function registerRegularTaskHistory(db: { regularTaskHistory?: RegularTaskHistoryModule }) {
  db.regularTaskHistory = regularTaskHistoryModule;
}

export { regularTaskHistoryModule as regularTaskHistory };
